/** Order timeline (event log) — Phase C1.
 *
 * Универсальная история заказа: статусы, оплаты, refund, fulfillment,
 * заметки, Bitrix sync и т.д. Источник истины — таблица order_events.
 */

import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  CheckCircle,
  CreditCard,
  FileText,
  History,
  Megaphone,
  RefreshCw,
  RotateCcw,
  ShoppingBag,
  User,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";

interface OrderEvent {
  id: number;
  event_type: string;
  actor_type: string;
  actor_id: number | null;
  actor_label: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

const EVENT_META: Record<
  string,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  order_placed: { label: "Заказ создан", icon: ShoppingBag, color: "text-success" },
  status_changed: { label: "Статус изменён", icon: Activity, color: "text-primary" },
  payment_captured: { label: "Оплата получена", icon: CreditCard, color: "text-success" },
  payment_failed: { label: "Ошибка оплаты", icon: XCircle, color: "text-destructive" },
  refund_granted: { label: "Возврат одобрен", icon: RotateCcw, color: "text-warning" },
  refund_sent: { label: "Возврат отправлен", icon: RotateCcw, color: "text-warning" },
  fulfillment_added: { label: "Отгрузка добавлена", icon: CheckCircle, color: "text-success" },
  note_added: { label: "Заметка", icon: FileText, color: "text-muted-foreground" },
  address_updated: { label: "Адрес обновлён", icon: Megaphone, color: "text-info" },
  invoice_generated: { label: "Инвойс создан", icon: FileText, color: "text-primary" },
  bitrix_synced: { label: "Bitrix синхронизация", icon: RefreshCw, color: "text-primary" },
  manager_assigned: { label: "Менеджер назначен", icon: User, color: "text-primary" },
  customer_notified: { label: "Клиент уведомлён", icon: Megaphone, color: "text-info" },
  cancelled: { label: "Отменён", icon: XCircle, color: "text-destructive" },
  other: { label: "Событие", icon: Activity, color: "text-muted-foreground" },
};

function formatActor(e: OrderEvent): string {
  if (e.actor_label) return e.actor_label;
  switch (e.actor_type) {
    case "system":
      return "Система";
    case "bitrix":
      return "Bitrix";
    case "payment_provider":
      return "Платёжный провайдер";
    case "customer":
      return "Клиент";
    case "staff":
      return e.actor_id ? `Менеджер #${e.actor_id}` : "Менеджер";
    default:
      return e.actor_type;
  }
}

function formatPayload(payload: Record<string, unknown>): string | null {
  const parts: string[] = [];
  if (payload.from_status && payload.to_status) {
    parts.push(`${payload.from_status} → ${payload.to_status}`);
  } else if (payload.to_status) {
    parts.push(`→ ${payload.to_status}`);
  }
  if (payload.reason) parts.push(String(payload.reason));
  if (payload.total_tiyin) {
    parts.push(`сумма: ${(Number(payload.total_tiyin) / 100).toLocaleString("ru")} сум`);
  }
  if (payload.delivery_method) parts.push(`доставка: ${payload.delivery_method}`);
  if (payload.payment_method) parts.push(`оплата: ${payload.payment_method}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function OrderTimeline({ orderId }: { orderId: string }): JSX.Element {
  const { data: events, isLoading } = useQuery({
    queryKey: ["order-events", orderId],
    queryFn: async () => {
      const { data } = await api.get(`/orders/${orderId}/events`);
      return data as OrderEvent[];
    },
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-4 h-4" />
          Timeline
          {events && (
            <Badge variant="secondary" className="text-[10px]">
              {events.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            Загрузка…
          </div>
        ) : !events || events.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            Событий ещё не было
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((e, i) => {
              const meta = EVENT_META[e.event_type] ?? EVENT_META.other!;
              const Icon = meta.icon;
              const details = formatPayload(e.payload);
              return (
                <div key={e.id} className="flex gap-3">
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`rounded-full bg-muted p-1.5 ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    {i < events.length - 1 && (
                      <div className="flex-1 w-px bg-border my-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      {meta.label}
                      <span className="text-xs text-muted-foreground">
                        · {formatActor(e)}
                      </span>
                    </div>
                    {details && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {details}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                      {new Date(e.created_at).toLocaleString("ru")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
